import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MoveRight, Loader2, Mail, MapPin, Linkedin } from "lucide-react";
import { useVariant } from "../../context/VariantContext";
import { INQUIRY_TYPES } from "../../data/content";
import { Reveal } from "./Reveal";
import { SectionEyebrow } from "./About";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", inquiry_type: INQUIRY_TYPES[0], message: "" };

export const Contact = () => {
  const { variant } = useVariant();
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const inputStyle = {
    background: "var(--surface)",
    borderColor: "var(--border)",
    color: "var(--text)",
    borderRadius: "var(--radius-tok)",
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you — your inquiry has been received.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const labelCls =
    "font-accent mb-2 block text-[10px] uppercase tracking-[0.2em] text-[color:var(--text-2)]";

  return (
    <section
      id="contact"
      data-testid="contact"
      className="relative border-t px-6 py-28 lg:px-10 lg:py-36"
      style={{ borderColor: "var(--border)" }}
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <SectionEyebrow index="08">Contact</SectionEyebrow>
            <h2 className="font-head mt-6 text-4xl leading-tight text-[color:var(--text)] sm:text-5xl lg:text-6xl">
              Let's talk about your piece.
            </h2>
            <p className="mt-6 max-w-md text-[15px] leading-relaxed text-[color:var(--text-2)]">
              Authentication, appraisal, advisory, expert testimony or a speaking
              engagement — share a few details and Graham's office will respond
              personally.
            </p>

            <div className="mt-10 space-y-4">
              <a
                href="mailto:hello@grahamwetzbarger.com"
                data-testid="contact-email"
                className="flex items-center gap-3 text-sm text-[color:var(--text)] transition-colors hover:text-[color:var(--accent)]"
              >
                <Mail size={16} style={{ color: "var(--accent)" }} />
                hello@grahamwetzbarger.com
              </a>
              <a
                href="https://www.linkedin.com/in/grahamwetzbarger/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-linkedin"
                className="flex items-center gap-3 text-sm text-[color:var(--text)] transition-colors hover:text-[color:var(--accent)]"
              >
                <Linkedin size={16} style={{ color: "var(--accent)" }} />
                Connect on LinkedIn
              </a>
              <p className="flex items-center gap-3 text-sm text-[color:var(--text-2)]">
                <MapPin size={16} style={{ color: "var(--accent)" }} />
                Newark, Delaware · Worldwide
              </p>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal delay={0.12}>
            <form onSubmit={submit} data-testid="contact-form" className="space-y-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className={labelCls}>Name *</label>
                  <input
                    data-testid="contact-name"
                    value={form.name}
                    onChange={set("name")}
                    className="w-full border px-4 py-3.5 text-sm outline-none focus:border-[var(--accent)]"
                    style={inputStyle}
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className={labelCls}>Email *</label>
                  <input
                    data-testid="contact-email-input"
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    className="w-full border px-4 py-3.5 text-sm outline-none focus:border-[var(--accent)]"
                    style={inputStyle}
                    placeholder="you@email.com"
                  />
                </div>
              </div>

              <div>
                <label className={labelCls}>Inquiry Type</label>
                <select
                  data-testid="contact-inquiry"
                  value={form.inquiry_type}
                  onChange={set("inquiry_type")}
                  className="w-full border px-4 py-3.5 text-sm outline-none focus:border-[var(--accent)]"
                  style={inputStyle}
                >
                  {INQUIRY_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelCls}>Message *</label>
                <textarea
                  data-testid="contact-message"
                  value={form.message}
                  onChange={set("message")}
                  rows={5}
                  className="w-full resize-none border px-4 py-3.5 text-sm outline-none focus:border-[var(--accent)]"
                  style={inputStyle}
                  placeholder="Tell us about the item, timeline and how we can help."
                />
              </div>

              <button
                type="submit"
                data-testid="contact-submit"
                disabled={loading}
                className="group inline-flex items-center gap-2 border px-8 py-4 text-xs font-medium uppercase tracking-[0.2em] transition-transform hover:-translate-y-0.5 disabled:opacity-60"
                style={{
                  background: "var(--accent)",
                  color: "var(--on-accent)",
                  borderColor: "var(--accent)",
                  borderRadius: "var(--radius-tok)",
                }}
              >
                {loading ? (
                  <>
                    <Loader2 size={15} className="animate-spin" /> Sending
                  </>
                ) : (
                  <>
                    Send Inquiry
                    <MoveRight size={15} className="transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
